import { useQuery } from '@tanstack/react-query'
import { domToPng } from 'modern-screenshot'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { IOS_DOWNLOAD_URL, PLAY_STORE_URL } from '../../../constants/appLinks'
import {
  fetchGlobalCardByPhone,
  searchCardsPeople,
  setCardsData,
} from '../../../functions/dataHelpers'
import type { FighterCard } from '../../../functions/store'
import { useCardsStore, useStore } from '../../../functions/store'
import ShareApp from '../ShareApp'
import Button from '../button'
import SmartSearch from '../shared/SmartSearch'
import Card from './Card'

type DashboardCardsProps = {
  user: {
    name: string
    phoneNumber: string
    belt: string
    step: string
  }
}

type SearchFighterItem = {
  id: string
  name: string
  phoneNumber?: string
  fighter: FighterCard
}

function DashboardCards({ user }: DashboardCardsProps) {
  const {
    cards: _cards,
    selectedFighter,
    selectedComparisonFighter,
    isStoreFilled,
    setSelectedFighter,
    setSelectedComparisonFighter,
    getFighterDataFromUserPhoneNumber,
  } = useCardsStore((state) => ({
    cards: state.cards,
    selectedFighter: state.selectedFighter,
    selectedComparisonFighter: state.selectedComparisonFighter,
    isStoreFilled: state.isStoreFilled,
    setCards: state.cardsActions.setCards,
    setBeltAveragesAsBenchmark: state.cardsActions.setBeltAveragesAsBenchmark,
    setSelectedFighter: state.cardsActions.setSelectedFighter,
    setSelectedComparisonFighter:
      state.cardsActions.setSelectedComparisonFighter,
    getFighterDataFromUserPhoneNumber:
      state.cardsActions.getFighterDataFromUserPhoneNumber,
  }))

  const { t } = useTranslation('avaliacao360')
  const userStore = useStore((state) => state)
  const cardsStore = useCardsStore((state) => state)
  const [showAppHint, setShowAppHint] = useState(false)
  const [cardUnavailableModal, setCardUnavailableModal] = useState(false)
  const [cardUnavailableMessage, setCardUnavailableMessage] = useState('')

  // Reset selected fighter when group changes
  useEffect(() => {
    setSelectedFighter(null)
  }, [userStore.group?._id, setSelectedFighter])

  // Load cards data
  const { isLoading, isFetching } = useQuery({
    queryKey: ['cardsData', userStore.phoneNumber, userStore.group?._id],
    queryFn: async () => {
      await setCardsData({
        cardsHook: cardsStore,
        userData: userStore,
        cardAlert: t('cards.cardUnavailable')?.toString() || 'Card indisponível',
        onCardUnavailable: (message: string) => {
          setCardUnavailableMessage(message)
          setCardUnavailableModal(true)
        },
      })
      return true
    },
    enabled: !!userStore.phoneNumber,
    staleTime: 5000,
  })

  const storeUrl = useMemo(() => {
    if (typeof navigator === 'undefined') return null
    const ua = navigator.userAgent.toLowerCase()
    if (ua.includes('android')) return PLAY_STORE_URL
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod'))
      return IOS_DOWNLOAD_URL
    return null
  }, [])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [autoRevealAttempted, setAutoRevealAttempted] = useState(false)

  // Reset autoRevealAttempted when group changes
  useEffect(() => {
    setAutoRevealAttempted(false)
  }, [userStore.group?._id])

  const [searchMode, setSearchMode] = useState<'view' | 'compare'>('compare')

  const COMPARISON_STORAGE_KEY = 'goodfellas:avaliacao360:cards:comparisonPhone'

  const beltLabel = (belt: string) => {
    const normalized = (belt || '').toLowerCase()
    if (!normalized) return t('table.notAvailable', 'N/D')

    if (normalized === 'white') return t('belts.white', 'Branca')
    if (normalized === 'blue') return t('belts.blue', 'Azul')
    if (normalized === 'purple') return t('belts.purple', 'Roxa')
    if (normalized === 'brown') return t('belts.brown', 'Marrom')
    if (normalized === 'black') return t('belts.black', 'Preta')

    // fallback: raw string
    return belt
  }

  // Effect to control body scroll
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isModalOpen])

  const revealCard = useCallback(() => {
    const fighterData = getFighterDataFromUserPhoneNumber(user.phoneNumber)
    if (!fighterData) return
    setSelectedFighter(fighterData)
  }, [getFighterDataFromUserPhoneNumber, setSelectedFighter, user.phoneNumber])

  function clearComparison() {
    setSelectedComparisonFighter(null)
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(COMPARISON_STORAGE_KEY)
    }
  }

  // Auto-reveal: if the card data is available, show the user's card without any click.
  useEffect(() => {
    if (!isStoreFilled) return
    if (selectedFighter) return
    if (autoRevealAttempted) return

    setAutoRevealAttempted(true)
    revealCard()
  }, [isStoreFilled, selectedFighter, autoRevealAttempted, revealCard])

  // Restore persisted comparison (best-effort)
  useEffect(() => {
    if (!isStoreFilled) return
    if (selectedComparisonFighter) return
    if (typeof window === 'undefined') return

    const persisted = window.localStorage.getItem(COMPARISON_STORAGE_KEY)
    if (!persisted) return
    const fighter = getFighterDataFromUserPhoneNumber(persisted)
    if (fighter) {
      setSelectedComparisonFighter(fighter)
    } else {
      window.localStorage.removeItem(COMPARISON_STORAGE_KEY)
    }
  }, [
    isStoreFilled,
    selectedComparisonFighter,
    getFighterDataFromUserPhoneNumber,
    setSelectedComparisonFighter,
  ])

  // Persist comparison selection
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!isStoreFilled) return

    if (selectedComparisonFighter?.phoneNumber) {
      window.localStorage.setItem(
        COMPARISON_STORAGE_KEY,
        selectedComparisonFighter.phoneNumber,
      )
    } else {
      window.localStorage.removeItem(COMPARISON_STORAGE_KEY)
    }
  }, [isStoreFilled, selectedComparisonFighter])

  function toggleModal() {
    if (isModalOpen) {
      document.body.style.overflow = 'unset'
    }
    setIsModalOpen(!isModalOpen)

    if (!isModalOpen) {
      setTimeout(() => {
        exportCard()
      }, 700)
    }
  }

  async function exportCard() {
    const cardRef = document.getElementById(
      'cardContainer',
    ) as HTMLElement | null
    const exportedCardContainer = document.getElementById('exportedCard')

    if (cardRef && exportedCardContainer) {
      if (exportedCardContainer.firstChild) {
        exportedCardContainer.removeChild(exportedCardContainer.firstChild)
      }

      const originalClasses = cardRef.className
      cardRef.classList.remove('lg:-rotate-3', 'lg:scale-[0.9]')
      cardRef.classList.remove('rotate-0')
      await new Promise((resolve) => requestAnimationFrame(resolve))

      try {
        const dataUrl = await domToPng(cardRef, {
          quality: 1.0,
          scale: 2,
          style: {
            transform: 'none',
            margin: '0',
          },
        })
        cardRef.className = originalClasses

        const img = new Image()
        img.onload = () => {
          img.style.maxWidth = '100%'
          img.style.height = 'auto'
          img.style.display = 'block'

          exportedCardContainer.appendChild(img)
        }
        img.onerror = (error) => {
          console.error('Error loading image data URL:', error)
          cardRef.className = originalClasses
        }
        img.src = dataUrl
      } catch (error) {
        cardRef.className = originalClasses
        console.error('modern-screenshot:', error)
      }
    } else {
      console.error('Card container or export container not found')
    }
  }

  async function shareImage() {
    const exportedCardContainer = document.getElementById('exportedCard')
    const imgElement = exportedCardContainer?.querySelector(
      'img',
    ) as HTMLImageElement | null

    if (
      !imgElement ||
      !imgElement.src ||
      !imgElement.src.startsWith('data:image/png')
    ) {
      return
    }

    try {
      const response = await fetch(imgElement.src)
      const blob = await response.blob()

      if (!blob) {
        return
      }

      const file = new File([blob], 'Goodfellas Card.png', { type: blob.type })
      const shareData = {
        files: [file],
        title: t('cards.shareTitle'),
        text: t('cards.shareText'),
      }

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        try {
          await navigator.share(shareData)
        } catch (err) {
          if (err.name !== 'AbortError') {
            triggerDownload(blob, 'Goodfellas Card.png')
          }
        }
      } else {
        triggerDownload(blob, 'Goodfellas Card.png')
      }
    } catch (error) {
      console.error('Error fetching or processing image for sharing:', error)
    }
  }

  function openAppToShareStory() {
    const groupId = userStore?.group?._id
    const url = groupId
      ? `goodfellas://evaluation/share?groupId=${encodeURIComponent(groupId)}`
      : `goodfellas://evaluation/share`

    // Attempt to open the native app. If it fails, fallback to existing web share/download flow.
    try {
      setShowAppHint(false)
      window.location.assign(url)
      setTimeout(() => {
        // If the browser is still visible, show a gentle hint.
        if (document.visibilityState === 'visible') {
          setShowAppHint(true)
        }
      }, 600)
    } catch (_e) {
      // ignore
    }
  }

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      {isStoreFilled && (
        <>
          <div className="flex flex-col lg:flex-row justify-center gap-8 lg:gap-4">
            {/* Card component to display the selected fighters */}
            <div className="w-full lg:w-1/2 flex flex-col items-center">
              {selectedFighter || isFetching ? (
                <>
                  <div
                    id="cardContainer"
                    className={`w-full max-w-[320px] sm:max-w-[420px] lg:max-w-lg h-auto flex justify-center items-center relative my-4 sm:my-6 transition-transform duration-500 ease-in-out ${
                      isModalOpen ? 'rotate-0' : 'lg:-rotate-3 lg:scale-[0.9]'
                    }`}
                  >
                    <div className="w-full h-full">
                      <Card
                        fighter={selectedFighter!}
                        comparison={selectedComparisonFighter}
                        isLoading={!selectedFighter || isFetching}
                      />
                    </div>
                  </div>
                  {selectedFighter && !isFetching && (
                    <Button
                      label={t('cards.exportCard')}
                      color="white"
                      action={toggleModal}
                    />
                  )}

                  {/* Export Modal - Full screen on mobile */}
                  {isModalOpen && (
                    <div
                      className="cardModal fixed inset-0 bg-black/95 flex flex-col justify-start items-center z-[1000] overflow-y-auto"
                      onClick={toggleModal}
                    >
                      {/* Close button - fixed position */}
                      <button
                        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                        onClick={toggleModal}
                        aria-label="Fechar"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>

                      <div
                        className="cardModalContent w-full max-w-lg mx-auto text-center px-4 py-8 sm:py-12"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Container for the exported image */}
                        <div
                          id="exportedCard"
                          className="mx-auto w-full h-auto bg-transparent flex justify-center items-center mb-4 sm:mb-6"
                          style={{ minHeight: '200px' }}
                        ></div>

                        <p className="text-white text-base sm:text-lg my-4 sm:my-6 px-4">
                          {t('cards.copyImage')}
                        </p>

                        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 px-4">
                          <Button
                            label={t('cards.copyImageShort')}
                            color="red"
                            action={shareImage}
                            className="w-full sm:w-auto"
                          />
                          <Button
                            label={t(
                              'cards.openInAppShareStory',
                              'Abrir no app (Instagram Stories)',
                            )}
                            color="instagram"
                            action={openAppToShareStory}
                            className="w-full sm:w-auto"
                          />
                          <Button
                            label={t('cards.close')}
                            color="white"
                            action={toggleModal}
                            className="w-full sm:w-auto"
                          />
                        </div>

                        {showAppHint ? (
                          <div className="mt-4 px-4 text-left max-w-md mx-auto">
                            <p className="text-white/80 text-sm">
                              {t(
                                'cards.openInAppHint',
                                'Se o app não abriu, instale/atualize o Goodfellas e tente novamente. No desktop, abra este link no celular.',
                              )}
                            </p>
                            {storeUrl ? (
                              <a
                                href={storeUrl}
                                className="inline-block mt-3 text-sm font-semibold text-white underline underline-offset-4"
                              >
                                {t('cards.openStore', 'Abrir loja')}
                              </a>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Minimal placeholder while auto-reveal runs (or if user isn't found)
                <div className="w-full max-w-[420px] lg:max-w-lg flex flex-col items-center gap-3 py-6">
                  <div className="card card-back text-white w-[280px] sm:w-[360px] md:w-[480px] h-[430px] sm:h-[555px] md:h-[740px] -rotate-3 transition-transform -my-16 sm:-my-24 md:-my-12 scale-75 sm:scale-90 md:scale-100 opacity-80">
                    <div className="card-main-element">
                      <div className="card-info w-[250px] sm:w-[320px] md:w-[430px]">
                        <div className="card-info-header">
                          <div className="card-info-header-belt"></div>
                          <div className="card-info-header-name"></div>
                        </div>
                        <div className="card-info-chart"></div>
                      </div>
                    </div>
                  </div>

                  <p className="text-white/80 text-sm sm:text-base text-center px-6">
                    {autoRevealAttempted
                      ? t('cards.cardUnavailable')
                      : 'Carregando seu card...'}
                  </p>

                  {/* Fallback: allow manual retry if the fighter wasn't found */}
                  {autoRevealAttempted && (
                    <Button
                      label={
                        t('cards.cardButtonReveal') || 'Recarregar meu card'
                      }
                      color="white"
                      action={revealCard}
                    />
                  )}

                  {/* While the card isn't ready, turn the empty state into a
                      growth loop: invite friends → they evaluate you → card. */}
                  {autoRevealAttempted && (
                    <div className="mt-4 flex flex-col items-center gap-2 border-t border-white/10 pt-5">
                      <p className="text-white/60 text-xs sm:text-sm text-center px-6 max-w-xs">
                        {t(
                          'shareApp.cardHint',
                          'Chame a galera pra treinar e te avaliar — seu card é gerado a partir das avaliações.',
                        )}
                      </p>
                      <ShareApp />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Minimal, action-oriented panel */}
            <div className="w-full lg:w-1/2 flex flex-col items-center justify-center gap-4 px-4 sm:px-0">
              <div className="w-full max-w-md">
                <div className="bg-slate-950/70 border border-white/15 rounded-2xl p-4 sm:p-5 backdrop-blur-md shadow-2xl">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-white text-base sm:text-lg font-semibold tracking-wide">
                      {searchMode === 'compare'
                        ? t('cards.compareWith', 'Comparar com')
                        : t('cards.viewCard', 'Ver card')}
                    </h3>
                    <p className="text-white/70 text-sm">
                      {searchMode === 'compare'
                        ? t('cards.compareHint', 'Busque alguém para comparar.')
                        : t('cards.viewHint', 'Busque alguém para ver o card.')}
                    </p>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setSearchMode('view')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                          searchMode === 'view'
                            ? 'bg-white/10 border-white/20 text-white'
                            : 'bg-transparent border-white/10 text-white/70 hover:text-white'
                        }`}
                      >
                        {t('cards.modeView', 'Ver')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSearchMode('compare')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                          searchMode === 'compare'
                            ? 'bg-white/10 border-white/20 text-white'
                            : 'bg-transparent border-white/10 text-white/70 hover:text-white'
                        }`}
                      >
                        {t('cards.modeCompare', 'Comparar')}
                      </button>
                    </div>

                    <SmartSearch
                      items={[] as SearchFighterItem[]}
                      remoteSearch={async (q) => {
                        const people = await searchCardsPeople(q)
                        return (
                          (people || [])
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            .filter((p: any) => p?.phoneNumber)
                            .filter(
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (p: any) => p.phoneNumber !== user.phoneNumber,
                            )
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            .map((p: any) => ({
                              id: String(p.phoneNumber),
                              name: String(p.name || p.phoneNumber),
                              phoneNumber: String(p.phoneNumber),
                              fighter: p as FighterCard,
                            }))
                        )
                      }}
                      onSelect={async (item: SearchFighterItem) => {
                        const card = (await fetchGlobalCardByPhone(
                          item.phoneNumber || item.fighter.phoneNumber,
                        )) as FighterCard | null
                        if (!card) return

                        if (searchMode === 'view') {
                          setSelectedFighter(card)
                        } else {
                          setSelectedComparisonFighter(card)
                        }
                      }}
                      placeholder={t(
                        'cards.globalSearchPlaceholder',
                        'Buscar atleta...',
                      )?.toString()}
                      searchKeys={['name', 'phoneNumber']}
                      remoteMinChars={2}
                    />
                  </div>

                  {selectedComparisonFighter && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="text-xs text-white/80 truncate">
                        <span className="font-medium text-white">
                          {selectedComparisonFighter.name}
                        </span>
                        {selectedComparisonFighter.belt ? (
                          <span className="ml-2 text-white/60">
                            • {beltLabel(selectedComparisonFighter.belt)}
                          </span>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={clearComparison}
                        className="text-xs text-white/80 hover:text-white underline underline-offset-4"
                      >
                        {t('cards.clearComparison', 'Limpar')}
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white text-sm font-semibold tracking-wide">
                      {t('cards.myCardScopeTitle', 'Seu card (global)')}
                    </h4>
                    <span className="text-white/60 text-xs">
                      {t(
                        'cards.myCardScopeHint',
                        'Baseado em todas as suas equipes',
                      )}
                    </span>
                  </div>

                  <div className="bg-slate-950/60 border border-white/15 rounded-2xl p-3 sm:p-4 backdrop-blur-md shadow-xl">
                    <div className="text-xs text-white/70">
                      {t(
                        'cards.myCardScopeBody',
                        'Este card é o mesmo em qualquer academia/grupo.',
                      )}
                    </div>
                    {selectedFighter?.phoneNumber !== user.phoneNumber ? (
                      <button
                        type="button"
                        className="mt-3 text-xs text-white/80 hover:text-white underline underline-offset-4"
                        onClick={revealCard}
                      >
                        {t('cards.backToMe', 'Voltar para meu card')}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Card Unavailable Modal */}
      {cardUnavailableModal && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-4"
          onClick={() => setCardUnavailableModal(false)}
        >
          <div
            className="bg-slate-900 border border-white/20 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-lg font-semibold mb-3">
              {t('cards.cardUnavailableTitle', 'Card indisponível')}
            </h3>
            <p className="text-white/80 text-sm mb-4">{cardUnavailableMessage}</p>
            <button
              onClick={() => setCardUnavailableModal(false)}
              className="w-full py-2 px-4 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
            >
              {t('common.close', 'Fechar')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default DashboardCards
